import axios from "axios";
import authHeader from "./authHeader";
import appNotificationService from "./utils/appNotificationService";
import AxiosService from "./axiosService";
import {useCacheStore} from "../stores/cacheStore";

const LAST_SYNC_KEY = "pappus-last-sync";

/**
 * Synchronisation hors ligne : le serveur reconstruit les données (progression au fil de l'eau), puis le client
 * les télécharge et remplace son cache. L'ancien cache reste utilisable jusqu'à ce que les nouvelles données
 * soient reçues : une synchronisation en échec ne le vide pas.
 */
class AppSyncService {

    url(target) {
        AxiosService.setEndPoint();
        return `${AxiosService.ENDPOINT}/appSync/${target}`;
    }

    /** Date (ms) de la dernière synchronisation réussie sur cet appareil, ou null */
    lastSync() {
        try {
            const value = Number(localStorage.getItem(LAST_SYNC_KEY));
            return value || null;
        } catch (e) {
            return null;
        }
    }

    /** Synchronisation complète : reconstruction côté serveur, puis téléchargement */
    async syncCache() {
        return this.run(async (cacheStore) => {
            await this.rebuild(cacheStore);
            return this.download(cacheStore);
        });
    }

    /** Téléchargement de la dernière synchronisation faite sur le serveur, sans la reconstruire */
    async loadCachedEntries() {
        return this.run((cacheStore) => this.download(cacheStore));
    }

    async run(steps) {
        const cacheStore = useCacheStore();
        if (cacheStore.sync.isActive) return 0;
        cacheStore.startSync();
        try {
            const total = await steps(cacheStore);
            try {
                localStorage.setItem(LAST_SYNC_KEY, String(Date.now()));
            } catch (e) {
                // Stockage indisponible (navigation privée) : seule la date affichée manque
            }
            cacheStore.completeSyncSuccess(total);
            return total;
        } catch (error) {
            console.error('Synchronisation en échec :', error);
            cacheStore.completeSyncError(error.message);
            appNotificationService.pushError(error);
            throw error;
        }
    }

    /** Reconstruction côté serveur : une ligne JSON par étape, puis {done} ou {error} */
    async rebuild(cacheStore) {
        const response = await fetch(this.url('sync'), {method: 'POST', headers: authHeader()});
        if (!response.ok) throw new Error(`Synchronisation refusée par le serveur (${response.status})`);

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
            const {done, value} = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, {stream: true});
            const lines = buffer.split('\n');
            buffer = lines.pop();
            for (const line of lines) {
                if (!line.trim()) continue;
                let data;
                try {
                    data = JSON.parse(line);
                } catch (e) {
                    console.warn('Ligne de progression illisible :', line);
                    continue;
                }
                if (data.error) throw new Error(data.message || 'Erreur pendant la synchronisation');
                if (data.done) return;
                if (data.step && data.totalSteps) {
                    cacheStore.updateSyncProgress(data.step, data.message, data.totalSteps, data.progress);
                }
            }
        }
        throw new Error('La synchronisation a été interrompue');
    }

    /** Téléchargement des données puis remplacement du cache ; renvoie le nombre d'entrées */
    async download(cacheStore) {
        cacheStore.updateSyncProgress(0, 'Téléchargement…', 0, 0);
        // Axios direct : pas de passage par le cache de l'application
        const response = await axios.post(this.url('loadEntries'), {}, {
            headers: authHeader(),
            onDownloadProgress: ({loaded, total}) => {
                const mb = (loaded / 1048576).toFixed(1);
                cacheStore.updateSyncProgress(0, `Téléchargement… ${mb} Mo`, 0, total ? Math.round((loaded * 100) / total) : 0);
            }
        });
        const entries = response?.data?.entries;
        if (!Array.isArray(entries)) throw new Error('Données de synchronisation illisibles');

        // Décodage par lots : l'interface reste fluide
        const prepared = [];
        const CHUNK = 500;
        for (let i = 0; i < entries.length; i += CHUNK) {
            for (const entry of entries.slice(i, i + CHUNK)) {
                prepared.push({
                    method: entry.method,
                    target: entry.target,
                    params: {},
                    payload: typeof entry.payload === 'string' ? JSON.parse(entry.payload) : entry.payload,
                    response: {data: typeof entry.data === 'string' ? JSON.parse(entry.data) : entry.data, status: 200},
                });
            }
            cacheStore.updateSyncProgress(Math.min(i + CHUNK, entries.length), 'Préparation…', entries.length);
            await new Promise(resolve => setTimeout(resolve, 0));
        }

        // Nouvelles données reçues : l'ancien cache est remplacé (clear() remet la progression à zéro, on la relance)
        await cacheStore.clear();
        cacheStore.startSync();
        cacheStore.updateSyncProgress(0, 'Enregistrement…', prepared.length);
        await cacheStore.setBulk(prepared);
        await cacheStore.updateStorageInfo();
        return prepared.length;
    }
}

export default new AppSyncService();
